/**
 * Voice Test
 * Real-time voice testing for agents
 */

import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { 
  Phone, PhoneOff, Mic, MicOff, Volume2, ArrowLeft,
  AlertCircle, Loader2, User, Bot
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

export default function VoiceTest() {
  const [searchParams] = useSearchParams();
  const agentId = searchParams.get('agent');
  
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [transcript, setTranscript] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  
  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const micStreamRef = useRef(null);
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const nextPlayTimeRef = useRef(0);
  const connectTimeRef = useRef(null);

  useEffect(() => {
    loadAgents();
    return () => {
      disconnect();
    };
  }, []);

  useEffect(() => {
    if (agentId && agents.length > 0) {
      const agent = agents.find(a => a.id === agentId);
      if (agent) {
        setSelectedAgent(agent);
      }
    }
  }, [agentId, agents]);

  const loadAgents = async () => {
    try {
      const response = await api.getAgents();
      // Filter out intent-ivr agents (they use different testing)
      const voiceAgents = (response.data.agents || []).filter(
        a => a.provider !== 'intent-ivr' && a.is_active
      );
      setAgents(voiceAgents);
    } catch (error) {
      console.error('Failed to load agents:', error);
      toast.error('Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  const connect = async () => {
    if (!selectedAgent) {
      toast.error('Please select an agent');
      return;
    }

    try {
      setIsConnecting(true);
      
      // Get realtime token
      const tokenResponse = await api.getRealtimeToken(selectedAgent.id);
      const config = tokenResponse.data;
      
      setSessionId(config.session_id);
      connectTimeRef.current = Date.now();

      // Connect based on provider
      if (selectedAgent.provider === 'openai') {
        await connectOpenAI(config);
      } else if (selectedAgent.provider === 'deepgram') {
        await connectDeepgram(config);
      } else {
        // Custom provider - connect to bridge
        await connectBridge(config);
      }

    } catch (error) {
      console.error('Connection failed:', error);
      toast.error('Failed to connect: ' + error.message);
      setIsConnecting(false);
    }
  };

  const connectOpenAI = async (config) => {
    // For browser-based testing, we need ephemeral token from backend
    // This is a simplified version - full implementation needs ephemeral key API
    const wsUrl = `wss://api.openai.com/v1/realtime?model=${config.model}`;
    
    wsRef.current = new WebSocket(wsUrl, [
      'realtime',
      `openai-insecure-api-key.${process.env.REACT_APP_OPENAI_API_KEY}`,
      'openai-beta.realtime-v1'
    ]);

    wsRef.current.onopen = () => {
      console.log('OpenAI connected');
      setIsConnected(true);
      setIsConnecting(false);
      
      // Configure session
      wsRef.current.send(JSON.stringify({
        type: 'session.update',
        session: {
          modalities: ['text', 'audio'],
          instructions: config.instructions,
          voice: config.voice,
          input_audio_format: 'pcm16',
          output_audio_format: 'pcm16',
          turn_detection: {
            type: 'server_vad',
            threshold: 0.5,
            silence_duration_ms: 500
          }
        }
      }));
      
      // Send greeting if exists
      if (config.greeting) {
        wsRef.current.send(JSON.stringify({
          type: 'conversation.item.create',
          item: {
            type: 'message',
            role: 'user',
            content: [{ type: 'input_text', text: 'Hello' }]
          }
        }));
        wsRef.current.send(JSON.stringify({ type: 'response.create' }));
      }
      
      startMicrophone('openai');
    };

    wsRef.current.onmessage = (event) => {
      const message = JSON.parse(event.data);
      handleOpenAIMessage(message);
    };

    wsRef.current.onerror = (error) => {
      console.error('WebSocket error:', error);
      toast.error('Connection error');
    };

    wsRef.current.onclose = () => {
      console.log('OpenAI disconnected');
      setIsConnected(false);
    };
  };

  const connectDeepgram = async (config) => {
    // Connect to Deepgram Agent API
    const wsUrl = 'wss://agent.deepgram.com/agent';
    
    wsRef.current = new WebSocket(wsUrl, ['token', process.env.REACT_APP_DEEPGRAM_API_KEY]);

    wsRef.current.onopen = () => {
      console.log('Deepgram connected');
      
      // Send configuration
      wsRef.current.send(JSON.stringify({
        type: 'SettingsConfiguration',
        audio: {
          input: { encoding: 'linear16', sample_rate: 16000 },
          output: { encoding: 'linear16', sample_rate: 24000, container: 'none' }
        },
        agent: {
          listen: { model: config.model || 'nova-2' },
          speak: { model: config.voice || 'aura-asteria-en' },
          think: {
            provider: { type: 'open_ai' },
            model: 'gpt-4o-mini',
            instructions: config.instructions
          }
        }
      }));
    };

    wsRef.current.onmessage = (event) => {
      if (event.data instanceof Blob) {
        // Audio data
        event.data.arrayBuffer().then(buffer => {
          const pcm16 = new Int16Array(buffer);
          playAudioPCM(pcm16, 24000);
        });
      } else {
        const message = JSON.parse(event.data);
        handleDeepgramMessage(message);
      }
    };

    wsRef.current.onerror = (error) => {
      console.error('Deepgram error:', error);
      toast.error('Connection error');
    };

    wsRef.current.onclose = () => {
      setIsConnected(false);
    };
  };

  const connectBridge = async (config) => {
    const bridgeUrl = process.env.REACT_APP_BRIDGE_URL || 'ws://localhost:8080';
    
    wsRef.current = new WebSocket(bridgeUrl);

    wsRef.current.onopen = () => {
      console.log('Bridge connected');
      
      // Send configuration
      wsRef.current.send(JSON.stringify({
        type: 'configure',
        agent_id: selectedAgent.id,
        session_id: config.session_id
      }));
    };

    wsRef.current.onmessage = (event) => {
      const message = JSON.parse(event.data);
      
      if (message.type === 'configured') {
        setIsConnected(true);
        setIsConnecting(false);
        startMicrophone('custom');
      } else if (message.type === 'audio') {
        // Base64 PCM16 audio
        const bytes = atob(message.data);
        const pcm16 = new Int16Array(bytes.length / 2);
        for (let i = 0; i < pcm16.length; i++) {
          pcm16[i] = bytes.charCodeAt(i * 2) | (bytes.charCodeAt(i * 2 + 1) << 8);
        }
        playAudioPCM(pcm16, 24000);
      } else if (message.type === 'transcript') {
        setTranscript(prev => [...prev, {
          role: message.role,
          content: message.text,
          timestamp: new Date()
        }]);
      }
    };

    wsRef.current.onerror = (error) => {
      console.error('Bridge error:', error);
      toast.error('Connection error');
    };

    wsRef.current.onclose = () => {
      setIsConnected(false);
    };
  };

  const handleOpenAIMessage = (message) => {
    switch (message.type) {
      case 'session.created':
        console.log('Session created:', message.session.id);
        break;
        
      case 'response.audio.delta':
        if (message.delta) {
          playAudioBase64(message.delta);
        }
        break;
        
      case 'response.audio_transcript.delta':
        // Assistant speaking
        break;
        
      case 'conversation.item.input_audio_transcription.completed':
        setTranscript(prev => [...prev, {
          role: 'user',
          content: message.transcript,
          timestamp: new Date()
        }]);
        break;
        
      case 'response.done':
        if (message.response?.output?.[0]?.content?.[0]?.transcript) {
          setTranscript(prev => [...prev, {
            role: 'assistant',
            content: message.response.output[0].content[0].transcript,
            timestamp: new Date()
          }]);
        }
        break;
        
      case 'error':
        console.error('OpenAI error:', message);
        toast.error(message.error?.message || 'OpenAI error');
        break;
    }
  };

  const handleDeepgramMessage = (message) => {
    switch (message.type) {
      case 'Welcome':
        console.log('Deepgram welcome');
        break;
        
      case 'SettingsApplied':
        setIsConnected(true);
        setIsConnecting(false);
        startMicrophone('deepgram');
        break;
        
      case 'ConversationText':
        setTranscript(prev => [...prev, {
          role: message.role,
          content: message.content,
          timestamp: new Date()
        }]);
        break;
        
      case 'Error':
        console.error('Deepgram error:', message);
        toast.error(message.description || 'Deepgram error');
        break;
    }
  };

  const startMicrophone = async (provider) => {
    try {
      const sampleRate = provider === 'deepgram' ? 16000 : 24000;
      
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, sampleRate }
      });

      micStreamRef.current = stream;
      audioContextRef.current = new AudioContext({ sampleRate });

      const source = audioContextRef.current.createMediaStreamSource(stream);
      const processor = audioContextRef.current.createScriptProcessor(4096, 1, 1);
      
      // Audio level visualization
      const analyser = audioContextRef.current.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      
      const updateLevel = () => {
        if (!isConnected) return;
        analyser.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b) / dataArray.length;
        setAudioLevel(avg / 255);
        requestAnimationFrame(updateLevel);
      };
      updateLevel();

      processor.onaudioprocess = (e) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN || isMuted) return;

        const inputData = e.inputBuffer.getChannelData(0);
        const pcm16 = new Int16Array(inputData.length);
        
        for (let i = 0; i < inputData.length; i++) {
          const s = Math.max(-1, Math.min(1, inputData[i]));
          pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }

        if (provider === 'openai') {
          // OpenAI expects base64
          const bytes = new Uint8Array(pcm16.buffer);
          let binary = '';
          for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i]);
          }
          wsRef.current.send(JSON.stringify({
            type: 'input_audio_buffer.append',
            audio: btoa(binary)
          }));
        } else {
          // Deepgram expects raw binary
          wsRef.current.send(pcm16.buffer);
        }
      };

      source.connect(processor);
      processor.connect(audioContextRef.current.destination);

    } catch (error) {
      console.error('Microphone error:', error);
      toast.error('Failed to access microphone');
    }
  };

  const playAudioBase64 = (base64Audio) => {
    try {
      const binary = atob(base64Audio);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      audioQueueRef.current.push(bytes);
      
      if (!isPlayingRef.current) {
        playNextChunk();
      }
    } catch (error) {
      console.error('Audio decode error:', error);
    }
  };

  const playAudioPCM = (pcm16, sampleRate) => {
    audioQueueRef.current.push({ pcm16, sampleRate });
    if (!isPlayingRef.current) {
      playNextChunk();
    }
  };

  const playNextChunk = async () => {
    if (audioQueueRef.current.length === 0) {
      isPlayingRef.current = false;
      return;
    }

    isPlayingRef.current = true;
    const chunk = audioQueueRef.current.shift();

    try {
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new AudioContext({ sampleRate: 24000 });
      }

      let float32, sampleRate = 24000;
      
      if (chunk.pcm16) {
        // Already PCM16
        float32 = new Float32Array(chunk.pcm16.length);
        for (let i = 0; i < chunk.pcm16.length; i++) {
          float32[i] = chunk.pcm16[i] / (chunk.pcm16[i] < 0 ? 0x8000 : 0x7FFF);
        }
        sampleRate = chunk.sampleRate;
      } else {
        // Uint8Array from base64
        const pcm16 = new Int16Array(chunk.buffer);
        float32 = new Float32Array(pcm16.length);
        for (let i = 0; i < pcm16.length; i++) {
          float32[i] = pcm16[i] / (pcm16[i] < 0 ? 0x8000 : 0x7FFF);
        }
      }

      const audioBuffer = audioContextRef.current.createBuffer(1, float32.length, sampleRate);
      audioBuffer.getChannelData(0).set(float32);

      const source = audioContextRef.current.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContextRef.current.destination);
      
      source.onended = () => playNextChunk();
      source.start(0);

    } catch (error) {
      console.error('Playback error:', error);
      isPlayingRef.current = false;
      setTimeout(() => playNextChunk(), 50);
    }
  };

  const disconnect = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(track => track.stop());
      micStreamRef.current = null;
    }
    
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    setIsConnected(false);
    setTranscript([]);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/agents" className="text-gray-400 hover:text-gray-600">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Voice Test</h1>
          <p className="text-sm text-gray-500">
            Test your voice agent in real-time
          </p>
        </div>
      </div>

      {/* Agent Selection */}
      <div className="bg-white rounded-lg shadow p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Agent
        </label>
        <select
          value={selectedAgent?.id || ''}
          onChange={(e) => {
            const agent = agents.find(a => a.id === e.target.value);
            setSelectedAgent(agent);
          }}
          disabled={isConnected}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
        >
          <option value="">Choose an agent...</option>
          {agents.map(agent => (
            <option key={agent.id} value={agent.id}>
              {agent.name} ({agent.provider})
            </option>
          ))}
        </select>
        
        {selectedAgent && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900">{selectedAgent.name}</h3>
                <p className="text-sm text-gray-500">
                  Provider: {selectedAgent.provider} | Voice: {selectedAgent.voice || selectedAgent.deepgram_voice || selectedAgent.custom_voice}
                </p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                selectedAgent.provider === 'openai' ? 'bg-green-100 text-green-800' :
                selectedAgent.provider === 'deepgram' ? 'bg-blue-100 text-blue-800' :
                'bg-purple-100 text-purple-800'
              }`}>
                {selectedAgent.provider}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Call Controls */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center gap-6">
          {/* Mute Button */}
          <button
            onClick={toggleMute}
            disabled={!isConnected}
            className={`p-4 rounded-full transition-colors ${
              isConnected
                ? isMuted
                  ? 'bg-red-100 text-red-600 hover:bg-red-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
          </button>

          {/* Connect/Disconnect Button */}
          <button
            onClick={isConnected ? disconnect : connect}
            disabled={isConnecting || !selectedAgent}
            className={`p-6 rounded-full transition-all ${
              isConnected
                ? 'bg-red-500 hover:bg-red-600 text-white'
                : isConnecting
                ? 'bg-gray-300 text-gray-500 cursor-wait'
                : 'bg-green-500 hover:bg-green-600 text-white'
            } ${!selectedAgent ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isConnecting ? (
              <Loader2 className="w-8 h-8 animate-spin" />
            ) : isConnected ? (
              <PhoneOff className="w-8 h-8" />
            ) : (
              <Phone className="w-8 h-8" />
            )}
          </button>

          {/* Audio Level */}
          <div className="p-4 rounded-full bg-gray-100">
            <Volume2 className="w-6 h-6 text-gray-600" />
          </div>
        </div>

        {/* Audio Level Indicator */}
        {isConnected && (
          <div className="mt-6">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 transition-all duration-100"
                style={{ width: `${audioLevel * 100}%` }}
              />
            </div>
            <p className="text-center text-sm text-gray-500 mt-2">
              {isMuted ? 'Microphone muted' : 'Listening...'}
            </p>
          </div>
        )}

        {/* Status */}
        <div className="mt-4 text-center">
          <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
            isConnected
              ? 'bg-green-100 text-green-800'
              : 'bg-gray-100 text-gray-600'
          }`}>
            <span className={`w-2 h-2 rounded-full ${
              isConnected ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
            }`} />
            {isConnected ? 'Connected' : 'Disconnected'}
          </span>
        </div>
      </div>

      {/* Transcript */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="font-medium text-gray-900">Conversation</h3>
        </div>
        <div className="p-6 h-96 overflow-y-auto space-y-4">
          {transcript.length === 0 ? (
            <div className="text-center text-gray-500 py-12">
              <Bot className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Start a call to see the conversation transcript</p>
            </div>
          ) : (
            transcript.map((message, index) => (
              <div
                key={index}
                className={`flex gap-3 ${
                  message.role === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                {message.role !== 'user' && (
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-primary-600" />
                  </div>
                )}
                <div
                  className={`max-w-[70%] rounded-lg px-4 py-2 ${
                    message.role === 'user'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-900'
                  }`}
                >
                  <p>{message.content}</p>
                  <p className={`text-xs mt-1 ${
                    message.role === 'user' ? 'text-primary-200' : 'text-gray-400'
                  }`}>
                    {message.timestamp.toLocaleTimeString()}
                  </p>
                </div>
                {message.role === 'user' && (
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-gray-600" />
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Testing Instructions:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Select an agent and click the green phone button to start</li>
              <li>Allow microphone access when prompted</li>
              <li>Speak naturally - the agent will respond via audio</li>
              <li>Click the red button to end the call</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
